/**
 * Thin wrappers over the Cortex API's org endpoints (`/v1/orgs/me/*` and the
 * superuser `/v1/admin/*`). The console never talks to Keycloak admin REST
 * directly — the API authenticates as the `cortex-api` service account
 * downstream. Everything here takes the active `CortexClient` and returns
 * the parsed response.
 */
import type { CortexClient } from '@cortex/sdk';
import type {
  CreateOrgRequest,
  InviteOrgMemberRequest,
  OrgMember,
  OrgMemberRole,
  OrgView,
  RealmStats,
} from '@cortex/types';

// ── Org-scoped (any member) ──────────────────────────────────────────────────

export function getMyOrg(client: CortexClient): Promise<OrgView> {
  return client.http.get('v1/orgs/me').json<OrgView>();
}

export function listMyOrgMembers(client: CortexClient): Promise<OrgMember[]> {
  return client.http.get('v1/orgs/me/members').json<OrgMember[]>();
}

export async function inviteMyOrgMember(
  client: CortexClient,
  input: InviteOrgMemberRequest,
): Promise<void> {
  await client.http.post('v1/orgs/me/members/invite', { json: input });
}

export async function setMyOrgMemberEnabled(
  client: CortexClient,
  userId: string,
  enabled: boolean,
): Promise<void> {
  await client.http.patch(`v1/orgs/me/members/${userId}`, { json: { enabled } });
}

// ── Superuser, cross-org ─────────────────────────────────────────────────────

export function listAllOrgs(client: CortexClient): Promise<OrgView[]> {
  return client.http.get('v1/admin/orgs').json<OrgView[]>();
}

export function getOrgById(client: CortexClient, orgId: string): Promise<OrgView> {
  return client.http.get(`v1/admin/orgs/${orgId}`).json<OrgView>();
}

export async function createOrg(client: CortexClient, input: CreateOrgRequest): Promise<void> {
  await client.http.post('v1/admin/orgs', { json: input });
}

export async function setOrgEnabledAdmin(
  client: CortexClient,
  orgId: string,
  enabled: boolean,
): Promise<void> {
  await client.http.patch(`v1/admin/orgs/${orgId}`, { json: { enabled } });
}

export function listOrgMembersAdmin(client: CortexClient, orgId: string): Promise<OrgMember[]> {
  return client.http.get(`v1/admin/orgs/${orgId}/members`).json<OrgMember[]>();
}

export async function inviteOrgMemberAdmin(
  client: CortexClient,
  orgId: string,
  input: InviteOrgMemberRequest,
): Promise<void> {
  await client.http.post(`v1/admin/orgs/${orgId}/members/invite`, { json: input });
}

export async function setOrgMemberEnabledAdmin(
  client: CortexClient,
  orgId: string,
  userId: string,
  enabled: boolean,
): Promise<void> {
  await client.http.patch(`v1/admin/orgs/${orgId}/members/${userId}`, { json: { enabled } });
}

export async function setOrgMemberRoleAdmin(
  client: CortexClient,
  orgId: string,
  userId: string,
  role: OrgMemberRole,
): Promise<void> {
  await client.http.patch(`v1/admin/orgs/${orgId}/members/${userId}/role`, { json: { role } });
}

export function getRealmStats(client: CortexClient): Promise<RealmStats> {
  return client.http.get('v1/admin/realm-stats').json<RealmStats>();
}
